// SMD-658 universal topbar search dropdown (flag-gated replacement for
// the legacy showings-only TopbarSearch).
//
// One debounced request to /search/universal returns server-rendered HTML
// per category; this class renders the grouped sections and delegates all
// result-row clicks to SearchResultActions (SMD-439 side panel, messaging).
//
// @see SearchController#universal for the backend endpoint (CTR-01)
// @see app/views/search/universal/ for the category partials
// @see memory_bank/features/FT-SMD-658/feature.md

import FetchUtils from '../utils/fetch_utils';
import SearchResultActions from './search_result_actions';
import ShowingsViewPopup from '../application/dashboard/showings/view_popup';

class UniversalTopbarSearch {
  #resultActions;
  #showingsViewPopup;

  static DEBOUNCE_MS = 300;
  static MIN_QUERY_LENGTH = 3;

  constructor(container) {
    this.container = container;
    this.input = container.querySelector('.js-topbar-search-input');
    this.dropdown = container.querySelector('.js-topbar-search-dropdown');
    this.resultsContainer = container.querySelector('.js-topbar-search-results');
    this.clearButton = container.querySelector('.js-topbar-search-clear');
    this.allResultsLink = container.querySelector('.js-universal-search-all');

    // Rails-generated URLs
    this.searchUrl = container.dataset.searchUrl;
    this.resultsPageUrl = container.dataset.resultsUrl;

    this.debounceTimer = null;
    this.isOpen = false;
    this.wasDropdownOpenBeforeModal = false;

    // Cache for the last response: { query: string, categories: Array }
    this.searchCache = null;

    // Track in-flight request to cancel on new search
    this.abortController = null;
  }

  init() {
    this.#showingsViewPopup = new ShowingsViewPopup();
    this.#showingsViewPopup.init();
    this.#resultActions = new SearchResultActions({
      onBeforeOpen: () => { this.wasDropdownOpenBeforeModal = this.isOpen; },
      onAfterOpen: () => this.closeDropdown()
    });
    this.bindEvents();
    this.bindModalCloseListener();
  }

  bindEvents() {
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('focus', this.handleFocus.bind(this));
    this.clearButton.addEventListener('click', this.handleClear.bind(this));
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  // Listen for modal/popup close events to restore dropdown state
  bindModalCloseListener() {
    document.addEventListener('sm-modal:closed', () => this.restoreDropdownIfNeeded());
    document.addEventListener('two-way-messaging:closed', () => this.restoreDropdownIfNeeded());

    document.addEventListener('sm-modal:opened', (e) => {
      const container = e.detail?.modalContainer;
      if (container?.querySelector('.js-showing-view-popup')) {
        this.closeDropdown();
      }
      this.#showingsViewPopup?.init('.js-sm-modal');
    });

    // Refresh search results after a showing is edited in the side panel
    document.addEventListener('showing:updated', () => {
      this.searchCache = null;
      if (this.resultsContainer.innerHTML.trim()) {
        this.performSearch(this.input.value.trim());
      }
    });
  }

  restoreDropdownIfNeeded() {
    if (this.wasDropdownOpenBeforeModal) {
      // setTimeout avoids the close click triggering handleOutsideClick
      setTimeout(() => this.openDropdown(), 0);
      this.wasDropdownOpenBeforeModal = false;
    }
  }

  handleInput(e) {
    const query = e.target.value.trim();

    this.toggleClearButton(query.length > 0);

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (query.length === 0) {
        // Empty input shows the focus preview (upcoming showings).
        this.performSearch('');
      } else if (query.length < UniversalTopbarSearch.MIN_QUERY_LENGTH) {
        this.showStartState();
        this.toggleAllResultsLink('');
        this.openDropdown();
      } else {
        this.performSearch(query);
      }
    }, UniversalTopbarSearch.DEBOUNCE_MS);
  }

  handleFocus() {
    const query = this.input.value.trim();
    // Focusing the field (even empty) shows the preview: a blank query renders
    // the upcoming-showings focus preview.
    if (this.searchCache && this.searchCache.query === query) {
      this.renderResponse({ query: this.searchCache.query, categories: this.searchCache.categories });
      this.toggleAllResultsLink(this.searchCache.showAllLink ? this.searchCache.query : '');
      this.openDropdown();
    } else {
      this.performSearch(query);
    }
  }

  handleClear() {
    this.input.value = '';
    this.toggleClearButton(false);
    this.performSearch('');
    this.input.focus();
  }

  handleOutsideClick(e) {
    if (!this.container.contains(e.target)) {
      this.closeDropdown();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.closeDropdown();
      this.input.blur();
    } else if (e.key === 'Enter' && document.activeElement === this.input) {
      this.handleEnterKey(e);
    }
  }

  // Enter in the input opens the full results page for the current query
  handleEnterKey(e) {
    const query = this.input.value.trim();
    if (query.length < UniversalTopbarSearch.MIN_QUERY_LENGTH) {
      return;
    }

    e.preventDefault();
    window.location.assign(this.allResultsUrl(query));
  }

  allResultsUrl(query) {
    return `${this.resultsPageUrl}?q=${encodeURIComponent(query)}`;
  }

  async performSearch(query) {
    // Cancel any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.showMessage('Searching...');
    this.openDropdown();

    try {
      const params = new URLSearchParams({ q: query });
      const response = await fetch(`${this.searchUrl}?${params}`, {
        headers: {
          'X-CSRF-Token': FetchUtils.CSRFToken(),
          'Content-Type': 'application/json; charset=utf-8'
        },
        signal: this.abortController.signal
      });

      if (response.status === 422) {
        this.showStartState();
        this.toggleAllResultsLink('');
        return;
      }

      FetchUtils.checkResponseStatus(response);
      const json = await response.json();

      this.searchCache = { query: json.query, categories: json.categories, showAllLink: json.show_all_link };
      this.renderResponse(json);
      // The server decides: with <= 7 total matches everything is already
      // in the dropdown and the link stays hidden (REQ-08).
      this.toggleAllResultsLink(json.show_all_link ? json.query : '');
    } catch (err) {
      // Ignore abort errors (expected when a new search starts)
      if (err.name === 'AbortError') {
        return;
      }

      FetchUtils.handleResponseError(err);
      this.showMessage('Search failed. Please try again.');
      this.toggleAllResultsLink('');
    }
  }

  // Empty sections are not returned by the server. With nothing to show, a
  // real query falls back to the "no results" state, while a blank query (the
  // upcoming-showings focus preview with no upcoming) falls back to the plain
  // start state rather than an empty "Upcoming Showings" header.
  renderResponse({ categories, query }) {
    if (categories.length === 0) {
      if (query && query.trim().length > 0) {
        this.showEmptyState();
      } else {
        this.showStartState();
      }
      return;
    }

    this.renderCategories(categories);
  }

  // "No results" state per Figma SM-Search-bar 148:330
  showEmptyState() {
    this.renderStateScreen('search-x', 'No results found',
      'We couldn\'t find anything matching your search');
  }

  // "Start typing" state per Figma SM-Search-bar 148:273 (blank/short query)
  showStartState() {
    this.renderStateScreen('text-search', 'Start typing to search',
      'Enter at least 3 characters to start');
  }

  renderStateScreen(icon, title, text) {
    this.resultsContainer.innerHTML = `
      <div class="topbar-search__empty-state">
        <span class="topbar-search__empty-state-icon"><i data-lucide="${icon}"></i></span>
        <span class="topbar-search__empty-state-title">${title}</span>
        <span class="topbar-search__empty-state-text">${text}</span>
      </div>
    `;
    LayoutUtils.refreshIcons();
  }

  // Render one section per category: uppercase label + count badge + rows.
  // Count badge uses .number-label to match the dashboard headings and the
  // full search results page.
  renderCategories(categories) {
    const sections = categories.map(({ key, label, count, html }) => {
      // Showings search only covers the recency window (SearchEntry::RECENCY_WINDOW);
      // the blank-query preview shows future "Upcoming Showings" instead, where the
      // note would be wrong, so it is gated on the real-query label (Figma 2002:228).
      const subtitle = key === 'showings' && label === 'Showings'
        ? '<span class="topbar-search__section-subtitle">from the past year only</span>'
        : '';
      return `
      <section class="topbar-search__section">
        <header class="topbar-search__section-header">
          <span class="topbar-search__section-title sm-typestyle sm-typestyle--label-strong">${label}</span>
          <span class="number-label">${count}</span>
        </header>
        ${subtitle}
        ${html}
      </section>
    `;
    });

    this.resultsContainer.innerHTML = sections.join('');

    // Initialize Lucide icons for dynamically loaded content
    LayoutUtils.refreshIcons();

    this.#resultActions.bind(this.resultsContainer);
  }

  showMessage(text) {
    this.resultsContainer.innerHTML = `
      <div class="topbar-search__message">
        <p>${text}</p>
      </div>
    `;
  }

  // The footer link only makes sense for a searchable query
  toggleAllResultsLink(query) {
    if (!this.allResultsLink) return;

    if (query && query.length >= UniversalTopbarSearch.MIN_QUERY_LENGTH) {
      this.allResultsLink.setAttribute('href', this.allResultsUrl(query));
      LayoutUtils.show(this.allResultsLink);
    } else {
      LayoutUtils.hide(this.allResultsLink);
    }
  }

  openDropdown() {
    LayoutUtils.show(this.dropdown);
    // Lift the topbar stacking context above page-level sticky panels
    // (e.g. .listing-tools-panel, dashboard filter wraps) while open —
    // the dropdown's own z-index is scoped inside the topbar's context.
    this.container.closest('.topbar')?.classList.add('topbar--search-open');
    this.isOpen = true;
  }

  closeDropdown() {
    LayoutUtils.hide(this.dropdown);
    this.container.closest('.topbar')?.classList.remove('topbar--search-open');
    this.isOpen = false;
  }

  toggleClearButton(show) {
    // Query for search icon dynamically since lucide replaces <i> with <svg>
    const searchIcon = this.container.querySelector('.js-topbar-search-icon');
    if (show) {
      LayoutUtils.show(this.clearButton);
      LayoutUtils.hide(searchIcon);
    } else {
      LayoutUtils.hide(this.clearButton);
      LayoutUtils.show(searchIcon);
    }
  }
}

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function() {
  const searchContainer = document.querySelector('.js-universal-search');
  if (searchContainer) {
    const search = new UniversalTopbarSearch(searchContainer);
    search.init();
  }
});

export default UniversalTopbarSearch;
